/**
 * 远程控制仪表盘页面入口模块。
 */
import { AnimatePresence, motion } from "framer-motion";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import Swal from "sweetalert2";
import DeviceOverview from "@/components/remote/device-overview";
import FileTransfer from "@/components/remote/file-transfer";
import Menu from "@/components/remote/menu";
import { RemoteScreen } from "@/components/remote/remote-screen";
import { sendSdp } from "@/services/remote/signaling";
import {
	useConnectionStore,
	useSessionStore,
	useWebRTCStore,
} from "@/stores/remote";
import TopControl from "./top-control";

// 进入仪表盘后是否自动发起远控连接。提到模块级常量，
// 避免作为组件内变量进入各 hook 的依赖数组。
const AUTO_START = true;

/**
 * 仪表盘页面，提供远程连接、文件传输以及调试控制入口。
 */
export default function Dashboard() {
	const hasStarted = useRef(false);

	const currentRoomId = useConnectionStore((s) => s.currentRoomId);
	const startHeartbeat = useConnectionStore((s) => s.startHeartbeat);
	const stopHeartbeat = useConnectionStore((s) => s.stopHeartbeat);

	const connectionState = useWebRTCStore((state) => state.connectionState);

	const navigate = useNavigate();

	// eslint-disable-next-line no-unused-vars
	const [showFileTransfer, setShowFileTransfer] = useState(false);
	const [showScreenWrapper, setShowScreenWrapper] = useState(false);
	const [showLoader, setShowLoader] = useState(false);
	const [hideDeviceOverview, setHideDeviceOverview] = useState(AUTO_START);

	const deviceId = useSessionStore((state) => state.deviceId);
	const videoRef = useRef<HTMLVideoElement | null>(null);

	useEffect(() => {
		if (!currentRoomId) {
			navigate("/", { replace: true });
			return;
		}

		startHeartbeat();
		return () => stopHeartbeat();
	}, [currentRoomId, navigate, startHeartbeat, stopHeartbeat]);

	useEffect(() => {
		if (connectionState === "connected") {
			setShowScreenWrapper(true);
			setShowLoader(false);
		} else if (!showLoader && !AUTO_START) {
			setHideDeviceOverview(false);
		}
	}, [connectionState, showLoader]);

	const toggleFileTransfer = useCallback(() => {
		setShowFileTransfer((prev) => !prev);
		setShowScreenWrapper((prev) => !prev);
	}, []);

	/**
	 * 准备发送和交换 SDP 信息以建立 WebRTC 信令通道。
	 */
	const prepareRemote = useCallback(async () => {
		if (!currentRoomId) {
			navigate("/", { replace: true });
			return;
		}

		console.log("prepareRemote called");
		if (AUTO_START) {
			setHideDeviceOverview(true);
			setShowScreenWrapper(true);
		}
		setShowLoader(true);
		try {
			// 发送 sdp 信息
			await sendSdp();

			// 等待 ICE 连接成功：订阅 store 而非 200ms 轮询，最多等待 30 秒。
			await new Promise<void>((resolve, reject) => {
				if (useWebRTCStore.getState().iceConnectionState === "connected") {
					resolve();
					return;
				}

				let unsubscribe = () => {};
				const timeout = setTimeout(() => {
					unsubscribe();
					reject(new Error("timeout"));
				}, 30000);

				unsubscribe = useWebRTCStore.subscribe((state) => {
					if (state.iceConnectionState === "connected") {
						clearTimeout(timeout);
						unsubscribe();
						resolve();
					}
				});
			});
		} catch (e) {
			const error = e as Error;
			Swal.fire(error.message, "Please try again later", "error").then(() => {
				setHideDeviceOverview(false);
				navigate("/");
			});
		} finally {
			setShowLoader(false);
		}
	}, [currentRoomId, navigate]);

	// 根据配置，决定是否自动开始连接（仅挂载后触发一次，由 hasStarted 守卫）。
	useEffect(() => {
		if (AUTO_START && currentRoomId && !hasStarted.current) {
			hasStarted.current = true;
			prepareRemote();
		}
	}, [currentRoomId, prepareRemote]);

	const setFullScreen = () => {
		if (videoRef.current) {
			const element = videoRef.current as HTMLVideoElement & {
				webkitRequestFullScreen?: () => void;
			};
			element.webkitRequestFullScreen?.();
		}
	};

	if (!currentRoomId) {
		return null;
	}

	return (
		<>
			<Menu />
			<div className="flex-1 bg-slate-100 px-0 transition-all duration-1000">
				<div className="max-w-screen-xl mx-auto bg-white flex flex-col rounded p-4 mt-8">
					<div>
						<TopControl
							deviceId={deviceId}
							showFileTransfer={showFileTransfer}
							showScreenWrapper={showScreenWrapper}
							showLoader={showLoader}
							toggleFileTransfer={toggleFileTransfer}
							setFullScreen={setFullScreen}
							prepareRemote={prepareRemote}
						/>

						{/* 使用 AnimatePresence 控制进出场动画，避免直接卸载导致闪屏 */}
						<AnimatePresence mode="wait">
							{connectionState !== "connected" && !hideDeviceOverview && (
								<motion.div
									key="device-overview"
									initial={{ opacity: 0, y: -12 }}
									animate={{ opacity: 1, y: 0 }}
									exit={{ opacity: 0, y: -12 }}
									transition={{ duration: 0.15, ease: "linear" }}
								>
									<DeviceOverview />
								</motion.div>
							)}
						</AnimatePresence>

						<br />
					</div>
					{/* 连接期间常驻挂载:否则面板未展开时收不到 incoming offer 回调(声明了 receive 却看不到确认框)。
					    showFileTransfer 仅控制发送区显隐;接收确认/进度始终常驻(评审 P1)。 */}
					{connectionState === "connected" && (
						<FileTransfer showFileTransfer={showFileTransfer}></FileTransfer>
					)}

					{connectionState === "connected" && showScreenWrapper && (
						<div className="flex-1 min-h-0">
							<RemoteScreen videoRef={videoRef}></RemoteScreen>
						</div>
					)}
				</div>
			</div>
		</>
	);
}
